import React, { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Download, Plus, RefreshCw, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const defaultQuality = () => ({ lambda: 0.5, gamma: 1, kpis: [] });
const defaultTask = (index = 1) => ({
  title: `Demo Görev ${index}`,
  quota: 5,
  base_amount: 100,
  min_followers: 1000,
  A0: 100,
  B0: 100,
  a: 0.5,
  S0: 10000,
  C0: 100,
  alpha: 0.5,
  beta: 0.5,
  reach_quality: defaultQuality(),
  perf_quality: defaultQuality(),
});

const fmt = (value, digits = 2) => Number(value ?? 0).toLocaleString('en-US', {
  minimumFractionDigits: digits,
  maximumFractionDigits: digits,
});

const fmtInt = (value) => Number(value ?? 0).toLocaleString('en-US', {
  maximumFractionDigits: 0,
});

const clone = (value) => JSON.parse(JSON.stringify(value));

function validateTask(task, index) {
  const label = task.title || `Görev ${index + 1}`;
  const checks = [
    ['Kontenjan', task.quota, 1, 500],
    ['Baz TL', task.base_amount, 0.000001, Infinity],
    ['Minimum takipçi', task.min_followers, 0, Infinity],
    ['A0', task.A0, 0, Infinity],
    ['B0', task.B0, 0, Infinity],
    ['a', task.a, 0, 1],
    ['S0', task.S0, 0.000001, Infinity],
    ['C0', task.C0, 0.000001, Infinity],
    ['alpha', task.alpha, 0, 2],
    ['beta', task.beta, 0, 2],
    ['Erişim lambda', task.reach_quality.lambda, 0, 1],
    ['Erişim gamma', task.reach_quality.gamma, 0, 3],
    ['Performans lambda', task.perf_quality.lambda, 0, 1],
    ['Performans gamma', task.perf_quality.gamma, 0, 3],
  ];
  for (const [name, value, min, max] of checks) {
    const n = Number(value);
    if (!Number.isFinite(n) || n < min || n > max) return `${label}: ${name} geçerli aralıkta değil`;
  }
  for (const [key, blockLabel] of [['reach_quality', 'Erişim'], ['perf_quality', 'Performans']]) {
    const kpis = task[key]?.kpis || [];
    if (!kpis.length) continue;
    const total = kpis.reduce((sum, kpi) => sum + Number(kpi.weight || 0), 0);
    if (Math.abs(total - 100) > 0.001) return `${label}: ${blockLabel} KPI ağırlıkları toplamı 100 olmalı`;
    if (kpis.some(kpi => !String(kpi.name || '').trim() || Number(kpi.weight || 0) <= 0)) {
      return `${label}: ${blockLabel} KPI adı ve ağırlığı zorunlu`;
    }
  }
  return null;
}

function erf(value) {
  const sign = value < 0 ? -1 : 1;
  const x = Math.abs(value);
  const t = 1 / (1 + 0.3275911 * x);
  const y = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-x * x);
  return sign * y;
}

function normalCdf(z) {
  return 0.5 * (1 + erf(z / Math.sqrt(2)));
}

function taskFormulaSections(result) {
  const seen = new Set();
  return (result?.rows || []).filter(row => {
    if (seen.has(row.task_id)) return false;
    seen.add(row.task_id);
    return true;
  }).map(row => {
    const config = row.pricing_config || {};
    const a = Number(config.a ?? 0);
    return {
      task_id: row.task_id,
      task_title: row.task_title,
      formula: `Rj = ${fmt(row.task_base_amount, 2)} + [${fmt(a, 2)} × ${fmt(config.A0, 2)} × (Sj / ${fmt(config.S0, 2)})^${fmt(config.alpha, 2)} × h_erişim] + [${fmt(1 - a, 2)} × ${fmt(config.B0, 2)} × (Cj / ${fmt(config.C0, 2)})^${fmt(config.beta, 2)} × h_performans]`,
      reachQuality: `h_erişim = (1 + ${fmt(config.reach_quality?.lambda, 2)} × Q_erişim)^${fmt(config.reach_quality?.gamma, 2)}`,
      perfQuality: `h_performans = (1 + ${fmt(config.perf_quality?.lambda, 2)} × Q_performans)^${fmt(config.perf_quality?.gamma, 2)}`,
    };
  });
}

function qualityDetailSections(result) {
  const taskRows = {};
  (result?.rows || []).forEach(row => {
    taskRows[row.task_id] = [...(taskRows[row.task_id] || []), row];
  });

  return Object.entries(taskRows).flatMap(([taskId, rows]) => {
    const first = rows[0] || {};
    const config = first.pricing_config || {};
    return [
      {
        key: `${taskId}-reach`,
        taskId,
        taskTitle: first.task_title,
        label: 'Erişim Kalitesi',
        kpis: config.reach_quality?.kpis || [],
        stats: result?.summary?.reach_kpi_stats?.[taskId] || [],
        valuesKey: 'reach_kpis',
        qKey: 'Q_reach',
        hKey: 'h_reach',
        lambda: config.reach_quality?.lambda,
        gamma: config.reach_quality?.gamma,
        rows,
      },
      {
        key: `${taskId}-perf`,
        taskId,
        taskTitle: first.task_title,
        label: 'Performans Kalitesi',
        kpis: config.perf_quality?.kpis || [],
        stats: result?.summary?.perf_kpi_stats?.[taskId] || [],
        valuesKey: 'perf_kpis',
        qKey: 'Q_perf',
        hKey: 'h_perf',
        lambda: config.perf_quality?.lambda,
        gamma: config.perf_quality?.gamma,
        rows,
      },
    ].filter(section => section.kpis.length > 0);
  });
}

export default function AdminPoolModel() {
  const { session } = useAuth();
  const [paymentType, setPaymentType] = useState('Gelir Havuzu');
  const [poolAmount, setPoolAmount] = useState(10000);
  const [tasks, setTasks] = useState([defaultTask(1)]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const totalQuota = useMemo(
    () => tasks.reduce((sum, task) => sum + Number(task.quota || 0), 0),
    [tasks]
  );

  const updateTask = (index, field, value) => {
    setTasks(prev => prev.map((task, i) => (i === index ? { ...task, [field]: value } : task)));
  };

  const updateQuality = (taskIndex, block, field, value) => {
    setTasks(prev => prev.map((task, i) => {
      if (i !== taskIndex) return task;
      return { ...task, [block]: { ...task[block], [field]: value } };
    }));
  };

  const addKpi = (taskIndex, block) => {
    setTasks(prev => prev.map((task, i) => {
      if (i !== taskIndex) return task;
      const kpis = task[block]?.kpis || [];
      return {
        ...task,
        [block]: {
          ...task[block],
          kpis: [...kpis, { name: '', weight: kpis.length ? 0 : 100 }],
        },
      };
    }));
  };

  const updateKpi = (taskIndex, block, kpiIndex, field, value) => {
    setTasks(prev => prev.map((task, i) => {
      if (i !== taskIndex) return task;
      const kpis = [...(task[block]?.kpis || [])];
      kpis[kpiIndex] = { ...kpis[kpiIndex], [field]: value };
      return { ...task, [block]: { ...task[block], kpis } };
    }));
  };

  const removeKpi = (taskIndex, block, kpiIndex) => {
    setTasks(prev => prev.map((task, i) => {
      if (i !== taskIndex) return task;
      return {
        ...task,
        [block]: {
          ...task[block],
          kpis: (task[block]?.kpis || []).filter((_, idx) => idx !== kpiIndex),
        },
      };
    }));
  };

  const addTask = () => {
    setTasks(prev => [...prev, defaultTask(prev.length + 1)]);
  };

  const duplicateTask = (index) => {
    setTasks(prev => {
      const copy = clone(prev[index]);
      copy.title = `${copy.title} Kopya`;
      return [...prev, copy];
    });
  };

  const removeTask = (index) => {
    setTasks(prev => prev.length === 1 ? prev : prev.filter((_, i) => i !== index));
  };

  const simulate = async () => {
    if (Number(poolAmount) <= 0) {
      toast.error('Havuz tutarı 0’dan büyük olmalı');
      return;
    }
    for (let i = 0; i < tasks.length; i += 1) {
      const error = validateTask(tasks[i], i);
      if (error) {
        toast.error(error);
        return;
      }
    }
    if (!session?.access_token) {
      toast.error('Oturum bulunamadı');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/pool-model/simulate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          payment_type: paymentType,
          pool_amount: Number(poolAmount),
          tasks: tasks.map(task => ({
            ...task,
            quota: Number(task.quota),
            base_amount: Number(task.base_amount),
            min_followers: Number(task.min_followers),
            A0: Number(task.A0),
            B0: Number(task.B0),
            a: Number(task.a),
            S0: Number(task.S0),
            C0: Number(task.C0),
            alpha: Number(task.alpha),
            beta: Number(task.beta),
            reach_quality: {
              lambda: Number(task.reach_quality.lambda),
              gamma: Number(task.reach_quality.gamma),
              kpis: (task.reach_quality.kpis || []).map(kpi => ({ name: kpi.name, weight: Number(kpi.weight) })),
            },
            perf_quality: {
              lambda: Number(task.perf_quality.lambda),
              gamma: Number(task.perf_quality.gamma),
              kpis: (task.perf_quality.kpis || []).map(kpi => ({ name: kpi.name, weight: Number(kpi.weight) })),
            },
          })),
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Simülasyon hesaplanamadı');
      setResult(data);
      toast.success('Havuz modeli hesaplandı');
    } catch (error) {
      toast.error(error.message || 'Simülasyon hesaplanamadı');
    } finally {
      setLoading(false);
    }
  };

  const exportResults = () => {
    if (!result?.rows?.length) return;
    const rows = result.rows.map(row => ({
      görev: row.task_title,
      kullanıcı: row.full_name,
      takipçi: row.followers,
      click: row.conversion,
      baz_tl: row.task_base_amount,
      fS: row.calculation?.fS,
      gC: row.calculation?.gC,
      Q_reach: row.calculation?.Q_reach,
      h_reach: row.calculation?.h_reach,
      Q_perf: row.calculation?.Q_perf,
      h_perf: row.calculation?.h_perf,
      erişim_katkısı: row.calculation?.block1,
      performans_katkısı: row.calculation?.block2,
      Rj: row.submission_score,
      kullanıcı_toplam_skor: row.user_total_score,
      havuz_pay_yüzde: row.share_percent,
      havuz_pay_tl: row.pool_share_amount,
      min_tl: row.minimum_payout_amount,
      nihai_tl: row.payout_amount,
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Havuz Modeli');
    XLSX.writeFile(workbook, `havuz-modeli-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className="space-y-6" data-testid="admin-pool-model-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Havuz Modeli</h2>
          <p className="text-sm text-gray-600">Gelir ve bütçe havuzu hesaplamasını demo görevlerle test edin.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={addTask} data-testid="pool-model-add-task">
            <Plus className="mr-2 h-4 w-4" />
            Görev Ekle
          </Button>
          <Button onClick={simulate} disabled={loading} data-testid="pool-model-simulate">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Örnekleri Yenile / Hesapla
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Demo Kampanya</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Ücretlendirme Türü</Label>
            <Select value={paymentType} onValueChange={setPaymentType}>
              <SelectTrigger data-testid="pool-model-payment-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Gelir Havuzu">Gelir Havuzu</SelectItem>
                <SelectItem value="Bütçe Havuzu">Bütçe Havuzu</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{paymentType === 'Bütçe Havuzu' ? 'Demo Bütçe Tutarı' : 'Demo Gelir Tutarı'}</Label>
            <Input type="number" min="1" step="0.01" value={poolAmount} onChange={(e) => setPoolAmount(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Demo Kapsam</Label>
            <div className="rounded-md border bg-gray-50 px-3 py-2 text-sm text-gray-700">
              {tasks.length} görev / {fmtInt(totalQuota)} onaylı örnek görev
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {tasks.map((task, index) => (
          <Card key={`${task.title}-${index}`}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">{task.title || `Demo Görev ${index + 1}`}</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => duplicateTask(index)}>Kopyala</Button>
                <Button variant="outline" size="sm" onClick={() => removeTask(index)} disabled={tasks.length === 1}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-5 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Görev Adı</Label>
                  <Input value={task.title} onChange={(e) => updateTask(index, 'title', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Kontenjan</Label>
                  <Input type="number" min="1" max="500" value={task.quota} onChange={(e) => updateTask(index, 'quota', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">R0 Baz TL</Label>
                  <Input type="number" min="0.01" step="0.01" value={task.base_amount} onChange={(e) => updateTask(index, 'base_amount', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Minimum Takipçi</Label>
                  <Input type="number" min="0" value={task.min_followers} onChange={(e) => updateTask(index, 'min_followers', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">a Erişim Katsayısı</Label>
                  <Input type="number" min="0" max="1" step="0.01" value={task.a} onChange={(e) => updateTask(index, 'a', e.target.value)} />
                </div>
              </div>

              <div className="grid grid-cols-6 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">A0</Label>
                  <Input type="number" min="0" step="0.01" value={task.A0} onChange={(e) => updateTask(index, 'A0', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">S0</Label>
                  <Input type="number" min="1" value={task.S0} onChange={(e) => updateTask(index, 'S0', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">α</Label>
                  <Input type="number" min="0" max="2" step="0.01" value={task.alpha} onChange={(e) => updateTask(index, 'alpha', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">B0</Label>
                  <Input type="number" min="0" step="0.01" value={task.B0} onChange={(e) => updateTask(index, 'B0', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">C0</Label>
                  <Input type="number" min="1" value={task.C0} onChange={(e) => updateTask(index, 'C0', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">β</Label>
                  <Input type="number" min="0" max="2" step="0.01" value={task.beta} onChange={(e) => updateTask(index, 'beta', e.target.value)} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {[
                  ['reach_quality', 'Erişim Kalitesi'],
                  ['perf_quality', 'Performans Kalitesi'],
                ].map(([block, label]) => (
                  <div key={block} className="rounded-md border bg-gray-50 p-3">
                    <div className="mb-3 flex items-center justify-between">
                      <Label className="text-sm font-medium">{label}</Label>
                      <Button type="button" variant="outline" size="sm" onClick={() => addKpi(index, block)}>
                        <Plus className="mr-1 h-3 w-3" />
                        KPI
                      </Button>
                    </div>
                    <div className="mb-3 grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">λ</Label>
                        <Input type="number" min="0" max="1" step="0.01" value={task[block].lambda} onChange={(e) => updateQuality(index, block, 'lambda', e.target.value)} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">γ</Label>
                        <Input type="number" min="0" max="3" step="0.01" value={task[block].gamma} onChange={(e) => updateQuality(index, block, 'gamma', e.target.value)} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      {(task[block].kpis || []).map((kpi, kpiIndex) => (
                        <div key={`${block}-${kpiIndex}`} className="grid grid-cols-[1fr_90px_auto] gap-2">
                          <Input value={kpi.name} placeholder="Yan KPI adı" onChange={(e) => updateKpi(index, block, kpiIndex, 'name', e.target.value)} />
                          <Input type="number" min="0" max="100" step="0.01" value={kpi.weight} onChange={(e) => updateKpi(index, block, kpiIndex, 'weight', e.target.value)} />
                          <Button type="button" variant="outline" onClick={() => removeKpi(index, block, kpiIndex)}>
                            Sil
                          </Button>
                        </div>
                      ))}
                      {(task[block].kpis || []).length === 0 && (
                        <p className="text-xs text-gray-500">Yan KPI yoksa kalite çarpanı nötr kabul edilir.</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {result && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Rj Hesaplama Formülü</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {taskFormulaSections(result).map(section => (
                <div key={section.task_id} className="rounded-md border bg-gray-50 p-3">
                  <div className="mb-2 font-medium text-gray-900">{section.task_title}</div>
                  <div className="font-mono text-sm text-gray-800">{section.formula}</div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-600">
                    <div className="font-mono">{section.reachQuality}</div>
                    <div className="font-mono">{section.perfQuality}</div>
                  </div>
                </div>
              ))}
              <p className="text-xs text-gray-500">
                f(Sj) = (Sj / S0)^α, g(Cj) = (Cj / C0)^β. Cj 0 ise performans katkısı 0 kabul edilir.
              </p>
            </CardContent>
          </Card>

          <div className="grid grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-600">Kampanya Skoru</CardTitle></CardHeader>
              <CardContent><p className="text-2xl font-bold">{fmt(result.summary?.campaign_total_score, 2)}</p></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-600">Havuz Tutarı</CardTitle></CardHeader>
              <CardContent><p className="text-2xl font-bold">{fmt(result.summary?.pool_amount, 2)} TL</p></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-600">Toplam Hakediş</CardTitle></CardHeader>
              <CardContent><p className="text-2xl font-bold">{fmt(result.summary?.total_payout, 2)} TL</p></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-600">Garanti Farkı</CardTitle></CardHeader>
              <CardContent><p className="text-2xl font-bold">{fmt(result.summary?.guarantee_overage, 2)} TL</p></CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Görev Alt Toplamları</CardTitle>
              <Badge variant="secondary">{result.summary?.submission_count || 0} satır</Badge>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Görev</TableHead>
                      <TableHead className="text-right">Satır</TableHead>
                      <TableHead className="text-right">Toplam Skor</TableHead>
                      <TableHead className="text-right">Toplam TL</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(result.task_subtotals || []).map(subtotal => (
                      <TableRow key={subtotal.task_id}>
                        <TableCell>{subtotal.task_title}</TableCell>
                        <TableCell className="text-right">{subtotal.row_count}</TableCell>
                        <TableCell className="text-right">{fmt(subtotal.total_score, 2)}</TableCell>
                        <TableCell className="text-right">{fmt(subtotal.total_payout, 2)} TL</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Simülasyon Sonuçları</CardTitle>
              <Button variant="outline" onClick={exportResults}>
                <Download className="mr-2 h-4 w-4" />
                XLSX İndir
              </Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Görev</TableHead>
                      <TableHead>Kullanıcı</TableHead>
                      <TableHead className="text-right">Takipçi</TableHead>
                      <TableHead className="text-right">Click</TableHead>
                      <TableHead className="text-right">R0</TableHead>
                      <TableHead className="text-right">fS</TableHead>
                      <TableHead className="text-right">gC</TableHead>
                      <TableHead className="text-right">Q/h Erişim</TableHead>
                      <TableHead className="text-right">Q/h Perf</TableHead>
                      <TableHead className="text-right">Erişim Katkısı</TableHead>
                      <TableHead className="text-right">Perf Katkısı</TableHead>
                      <TableHead className="text-right">Rj</TableHead>
                      <TableHead className="text-right">Kullanıcı Toplam</TableHead>
                      <TableHead className="text-right">Pay %</TableHead>
                      <TableHead className="text-right">Pay TL</TableHead>
                      <TableHead className="text-right">Min TL</TableHead>
                      <TableHead className="text-right">Nihai TL</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(result.rows || []).map(row => (
                      <TableRow key={row.submission_id}>
                        <TableCell className="min-w-[140px]">{row.task_title}</TableCell>
                        <TableCell className="min-w-[140px]">{row.full_name}</TableCell>
                        <TableCell className="text-right">{fmtInt(row.followers)}</TableCell>
                        <TableCell className="text-right">{fmtInt(row.conversion)}</TableCell>
                        <TableCell className="text-right">{fmt(row.task_base_amount, 2)}</TableCell>
                        <TableCell className="text-right">{fmt(row.calculation?.fS, 4)}</TableCell>
                        <TableCell className="text-right">{fmt(row.calculation?.gC, 4)}</TableCell>
                        <TableCell className="text-right">{fmt(row.calculation?.Q_reach, 4)} / {fmt(row.calculation?.h_reach, 4)}</TableCell>
                        <TableCell className="text-right">{fmt(row.calculation?.Q_perf, 4)} / {fmt(row.calculation?.h_perf, 4)}</TableCell>
                        <TableCell className="text-right">{fmt(row.calculation?.block1, 2)}</TableCell>
                        <TableCell className="text-right">{fmt(row.calculation?.block2, 2)}</TableCell>
                        <TableCell className="text-right font-medium">{fmt(row.submission_score, 2)}</TableCell>
                        <TableCell className="text-right">{fmt(row.user_total_score, 2)}</TableCell>
                        <TableCell className="text-right">{fmt(row.share_percent, 4)}</TableCell>
                        <TableCell className="text-right">{fmt(row.pool_share_amount, 2)}</TableCell>
                        <TableCell className="text-right">{fmt(row.minimum_payout_amount, 2)}</TableCell>
                        <TableCell className="text-right font-medium">{fmt(row.payout_amount, 2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Kalite Metriği Hesap Detayları</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="rounded-md border bg-gray-50 p-3 text-xs text-gray-600">
                <div className="font-mono">z = (x - μ) / (σ + ε)</div>
                <div className="font-mono">q = 2 × CDF(z) - 1</div>
                <div className="font-mono">Q = Σ(q × KPI ağırlığı)</div>
                <div className="font-mono">h(Q) = (1 + λ × Q)^γ</div>
              </div>

              {qualityDetailSections(result).length === 0 ? (
                <p className="text-sm text-gray-500">Bu simülasyonda yan kalite KPI tanımlı değil; erişim ve performans kalite çarpanları nötr olarak 1 alındı.</p>
              ) : (
                qualityDetailSections(result).map(section => (
                  <div key={section.key} className="space-y-3 rounded-md border p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">{section.taskTitle} - {section.label}</div>
                        <div className="text-xs text-gray-500">λ={fmt(section.lambda, 2)} / γ={fmt(section.gamma, 2)}</div>
                      </div>
                      <Badge variant="secondary">{section.kpis.length} KPI</Badge>
                    </div>

                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Kullanıcı</TableHead>
                            <TableHead>KPI</TableHead>
                            <TableHead className="text-right">Ağırlık</TableHead>
                            <TableHead className="text-right">x</TableHead>
                            <TableHead className="text-right">μ</TableHead>
                            <TableHead className="text-right">σ</TableHead>
                            <TableHead className="text-right">z</TableHead>
                            <TableHead className="text-right">q</TableHead>
                            <TableHead className="text-right">Ağırlıklı q</TableHead>
                            <TableHead className="text-right">Q</TableHead>
                            <TableHead className="text-right">h</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {section.rows.flatMap(row => section.kpis.map((kpi, kpiIndex) => {
                            const x = Number(row[section.valuesKey]?.[kpiIndex] ?? 0);
                            const stat = section.stats[kpiIndex] || {};
                            const mean = Number(stat.mean ?? 0);
                            const std = Number(stat.std ?? 0);
                            const zRaw = std <= 0.000001 ? 0 : (x - mean) / (std + 1e-9);
                            const z = Math.max(-3, Math.min(3, zRaw));
                            const q = std <= 0.000001 ? 0 : (2 * normalCdf(z)) - 1;
                            const weightRatio = Number(kpi.weight || 0) / 100;
                            return (
                              <TableRow key={`${section.key}-${row.submission_id}-${kpiIndex}`}>
                                <TableCell>{row.full_name}</TableCell>
                                <TableCell>{kpi.name}</TableCell>
                                <TableCell className="text-right">{fmt(kpi.weight, 2)}%</TableCell>
                                <TableCell className="text-right">{fmt(x, 2)}</TableCell>
                                <TableCell className="text-right">{fmt(mean, 2)}</TableCell>
                                <TableCell className="text-right">{fmt(std, 4)}</TableCell>
                                <TableCell className="text-right">{fmt(z, 4)}</TableCell>
                                <TableCell className="text-right">{fmt(q, 4)}</TableCell>
                                <TableCell className="text-right">{fmt(q * weightRatio, 4)}</TableCell>
                                <TableCell className="text-right">{fmt(row.calculation?.[section.qKey], 4)}</TableCell>
                                <TableCell className="text-right">{fmt(row.calculation?.[section.hKey], 4)}</TableCell>
                              </TableRow>
                            );
                          }))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
